import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { HeroImage } from "../../Atoms/HeroImage";
import { StateProvider } from "../../SharedContext";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { collectionToStories } from "@quintype/components";
import { CollectionName } from "../../Atoms/CollectionName";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { generateNavigateSlug, getSlot, getTextColor, navigateTo, rgbToHex } from "../../../utils/utils";
import { ProgressiveHydration } from "../../../hydration-component";
import { LoadMoreTarget } from "../../Atoms/LoadMoreTarget";
import { useDeviceType } from "../../../../components/customHooks/useDeviceType";
import { SvgIconHandler } from "../../../../components/atoms/SvgIconHandler";
import { getCategoryAndSubCategory, useTrackWidgetImpression } from "../../../../ga-utils";
import { getTemplateName } from "../../../../components/utils";
import "./four-col-five-stories.m.css";

const loadMore = ({ isLoading, renderStories, getMoreStories, subsequentLoadCount = 4 }) => {
  if (isLoading) return;
  const limit = subsequentLoadCount;
  const offset = renderStories.length + 1;
  getMoreStories(offset, limit);
};

const FourColFiveStories = ({
  collection,
  config,
  getMoreStories,
  isLoadMoreVisible,
  isLoading,
  isolatedLoadMore,
  hideFirstCard,
  separator,
  showMultipleAuthor = false,
  pageType,
}) => {
  const stories = collectionToStories(collection);
  if (!stories.length) return null;

  const {
    collectionNameBorderColor = "",
    theme = "",
    slotConfig = [],
    collectionNameTemplate = "",
    footerButton = "",
    borderColor = "",
    subsequentLoadCount = 4,
  } = config;
  const { type = "story", component } = get(slotConfig, [0], {});
  const textColor = getTextColor(theme);
  const sectionTagBorderColor = rgbToHex(borderColor);
  const adWidgetSlot = type === "ad" || type === "widget" ? getSlot(type, component) : null;

  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));

  const [firstStory, ...otherStoriesAll] = stories || [];
  const otherStories = otherStoriesAll.slice(0, 4);

  const handleStoryClick = (story, position) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const storyUrl = `/${story.slug}`;
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: position,
        section_name: collection?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
        eventCallback: function () {
          window.location.href = storyUrl;
        },
      });
    }
  };

  const handleReadMoreClick = () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: -1,
        section_name: collection?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const handleSectionClick = () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: -2,
        section_name: collection?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const isBrowser = typeof window !== "undefined";

  // Use the custom hook for tracking widget impressions
  const componentRef = useTrackWidgetImpression(
    {
      sectionName: collection?.name,
      pageType: pageType,
      location: isBrowser ? window.location : null,
      config: qtConfig,
      threshold: 0.2,
    },
    { disabled: !isBrowser }
  );

  const storyCard = (story, showSubHeadLine = false, showAuthorContent) => (
    <>
      <HeroImage config={config} story={story} aspectRatio={[[16, 9]]} />
      <StorycardContent
        theme={theme}
        story={story}
        config={config}
        headerLevel={6}
        borderColor={sectionTagBorderColor}
        showSubHeadLine={showSubHeadLine}
        separator={separator}
        showMultipleAuthor={showMultipleAuthor}
        showAuthorContent={showAuthorContent}
      />
    </>
  );

  const otherCards = (story, index) => (
    <div
      styleName="card"
      data-test-id="story-card"
      key={`four-col-five-story-${index}-${get(story, ["id"], "0")}`}
      onClick={() => handleStoryClick(story, index + 2)}
    >
      {storyCard(story, false, false)}
    </div>
  );

  const getLoadMore = (opts) => {
    if (!isLoadMoreVisible) return null;

    if (isolatedLoadMore) {
      return (
        <LoadMoreTarget
          collection={collection}
          componentName={"FourColFiveStories"}
          offset={stories.length}
          limit={subsequentLoadCount}
        />
      );
    }

    const url = generateNavigateSlug(collection, qtConfig);
    return (
      <ProgressiveHydration>
        <div onClick={handleReadMoreClick}>
          <LoadmoreButton
            config={config}
            collection={collection}
            template={footerButton}
            onClick={() => loadMore(opts)}
            navigate={() => navigateTo(dispatch, url)}
            qtConfig={qtConfig}
          />
        </div>
      </ProgressiveHydration>
    );
  };

  const renderStories = hideFirstCard ? stories : otherStories;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  return (
    <div
      className="full-width-with-padding arrow-component"
      data-test-id="four-col-five-stories"
      style={{ backgroundColor: theme, color: textColor }}
      ref={componentRef}
    >
      <div styleName="four-col-five-stories">
        <div onClick={handleSectionClick}>
          <CollectionName
            collection={collection}
            collectionNameTemplate={collectionNameTemplate}
            collectionNameBorderColor={collectionNameBorderColor}
            headerLevel={2}
          />
        </div>
        {isDesktop && (
          <div className="load-more-button">
            {getLoadMore({ isLoading, renderStories, getMoreStories, subsequentLoadCount })}
            <SvgIconHandler type="arrow" width="24" height="24" viewBox="0 0 24 24" />
          </div>
        )}
        <div className="stories-wrapper" styleName="wrapper">
          {!hideFirstCard && (
            <div
              className="first-card"
              styleName="first-card"
              data-test-id="story-card"
              onClick={() => handleStoryClick(firstStory, 1)}
            >
              {storyCard(firstStory, isDesktop, false)}
            </div>
          )}
          <div className="other-cards" styleName="other-cards">
            {renderStories.slice(0, 3).map((story, index) => otherCards(story, index, separator))}
            {renderStories.slice(3).map((story, index) => otherCards(story, index + 3, separator))}
          </div>
          {!isDesktop && getLoadMore({ isLoading, renderStories, getMoreStories, subsequentLoadCount })}
        </div>
      </div>
    </div>
  );
};

FourColFiveStories.propTypes = {
  collection: PropTypes.object,
  config: PropTypes.shape({
    collectionNameBorderColor: PropTypes.string,
    theme: PropTypes.string,
    slotConfig: PropTypes.array,
    collectionNameTemplate: PropTypes.string,
    footerButton: PropTypes.string,
    borderColor: PropTypes.string,
    subsequentLoadCount: PropTypes.number,
  }),
  getMoreStories: PropTypes.func,
  isLoading: PropTypes.bool,
  hideFirstCard: PropTypes.bool,
  isLoadMoreVisible: PropTypes.bool,
  isolatedLoadMore: PropTypes.bool,
  showMultipleAuthor: PropTypes.bool,
  pageType: PropTypes.string,
  separator: PropTypes.bool,
};

FourColFiveStories.defaultProps = {
  getMoreStories: () => {},
  isLoadMoreVisible: true,
  isLoading: false,
  collection: {},
  config: {},
  hideFirstCard: false,
  pageType: "",
};

export default StateProvider(FourColFiveStories);
